/**
 * DayStrip — kompakte Tagesübersicht, nur mobil (ab `md` ausgeblendet).
 *
 * Fünf Buttons (Mo–Fr), je mit zwei kleinen Status-Quadraten (Hin-/Rückweg).
 * Jeder Button scrollt zur zugehörigen Tageskarte (`#day-N`). Das Scrollen läuft
 * über `scrollIntoView` statt über einen `#`-Anker, damit die URL (und der
 * History-Stack des Routers) unverändert bleibt — ein Hash-Anker würde einen
 * fremden History-Eintrag erzeugen und beim Zurück-Navigieren den `useBlocker`
 * in WeeklyPlanView mit einer unbekannten POP-Location kollidieren lassen.
 * Ohne Eintrag wird ein gestrichelter, leerer Rahmen gezeigt (kein Icon).
 */

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useI18n } from "../../i18n/I18nProvider";
import { STATUS_META, type AvailabilityStatus } from "../../theme/status";
import { DAY_LABEL_KEYS, statusOf } from "./helpers";
import type { DayView } from "./types";

function Dot({ status }: { status: AvailabilityStatus | null }) {
  const meta = status ? STATUS_META[status] : null;
  const Icon = meta?.icon;
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: 1,
        display: "grid",
        placeItems: "center",
        color: "#fff",
        bgcolor: meta ? `${meta.color}.main` : "transparent",
        border: meta ? 0 : "2px dashed",
        borderColor: "divider",
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 16 }} /> : null}
    </Box>
  );
}

export default function DayStrip({
  days,
  displayedUserId,
  dayLabels,
}: {
  days: DayView[];
  displayedUserId: string | undefined;
  /** Vollständige Wochentagsnamen für die aria-labels. */
  dayLabels: string[];
}) {
  const { t } = useI18n();
  return (
    <Stack
      direction="row"
      gap={0.75}
      px={1.5}
      pb={1.5}
      pt={0.5}
      sx={{ display: { xs: "flex", md: "none" } }}
    >
      {days.map((day, i) => (
        <Button
          key={day.date ?? i}
          onClick={() =>
            document
              .getElementById(`day-${i}`)
              ?.scrollIntoView({ behavior: "smooth", block: "start" })
          }
          aria-label={dayLabels[i] ?? t(DAY_LABEL_KEYS[i] ?? DAY_LABEL_KEYS[0])}
          sx={{
            flex: 1,
            flexDirection: "column",
            gap: 0.5,
            bgcolor: "action.hover",
            minHeight: 60,
            px: 0,
            color: "text.primary",
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            {t(DAY_LABEL_KEYS[i] ?? DAY_LABEL_KEYS[0])}
          </Typography>
          <Stack direction="row" gap={0.5}>
            <Dot status={statusOf(day.morning, displayedUserId)} />
            <Dot status={statusOf(day.afternoon, displayedUserId)} />
          </Stack>
        </Button>
      ))}
    </Stack>
  );
}
